import React, { useState, useEffect } from 'react';
import { format, startOfYear, endOfYear, startOfMonth, endOfMonth, parse, addMonths, eachMonthOfInterval } from 'date-fns';
import { nl } from 'date-fns/locale';
import { FileText, Download, Calendar, TrendingUp } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../lib/supabase';
import { useAccentColor } from '../lib/useAccentColor';
import ComparisonView from './ComparisonView';

// Use Arial as the standard font
const FONT = 'helvetica';

// PDF styling
const COLORS = {
  background: '#FFFFFF',
  card: '#F3F4F6',
  text: '#111827',
  textSecondary: '#6B7280',
  accent: '#3B82F6',
  border: '#E5E7EB',
};

const STYLES = {
  cardPadding: 12,
  cardRadius: 4,
  tablePadding: 2,
  fontSize: {
    title: 13,
    subtitle: 10,
    body: 9,
    small: 8
  }
};

interface RevenueEntry {
  date: string;
  cash_revenue: number;
  card_revenue: number;
  invoice_revenue: number;
}

interface RevenueTypes {
  cash: boolean;
  card: boolean;
  invoice: boolean;
  tips: boolean;
}

export default function Reports() {
  const [loading, setLoading] = useState(false);
  const [loadingYearly, setLoadingYearly] = useState(false);
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState((now.getMonth() + 1).toString().padStart(2, '0'));
  const [selectedYearlyYear, setSelectedYearlyYear] = useState(now.getFullYear().toString());
  const [selectedTypes, setSelectedTypes] = useState<RevenueTypes>({
    cash: true,
    card: true,
    invoice: true,
    tips: false
  });
  const [selectedYearlyTypes, setSelectedYearlyTypes] = useState<RevenueTypes>({
    cash: true,
    card: true,
    invoice: true,
    tips: false
  });
  const { accentColor } = useAccentColor();

  // Generate available years (2024 - current year)
  const startYear = 2024;
  const currentYear = now.getFullYear();
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => startYear + i);

  // Generate months for selected year
  const getAvailableMonths = () => {
    const year = parseInt(selectedYear);
    if (year === currentYear) {
      // Current year: show months up to current month
      return Array.from({ length: now.getMonth() + 1 }, (_, i) => i + 1);
    } else if (year === startYear) {
      // Start year 2024: show all 12 months
      return Array.from({ length: 12 }, (_, i) => i + 1);
    } else {
      // Other years: show all 12 months
      return Array.from({ length: 12 }, (_, i) => i + 1);
    }
  };

  // Update selected month when year changes
  useEffect(() => {
    const availableMonths = getAvailableMonths();
    const currentMonth = parseInt(selectedMonth);
    if (!availableMonths.includes(currentMonth)) {
      // If current selected month is not available, select the last available month
      setSelectedMonth(availableMonths[availableMonths.length - 1].toString().padStart(2, '0'));
    }
  }, [selectedYear]);


  const generateMonthlyReport = async () => {
    setLoading(true);
    const date = parse(`${selectedYear}-${selectedMonth}`, 'yyyy-MM', new Date());
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('revenue_entries')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', format(monthStart, 'yyyy-MM-dd'))
      .lte('date', format(monthEnd, 'yyyy-MM-dd'))
      .order('date', { ascending: true });

    if (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
      return;
    }

    // Fetch tips (hidden revenue) if tips is enabled
    let tipsMap: Record<string, number> = {};
    if (selectedTypes.tips) {
      const { data: tipsData } = await supabase
        .from('hidden_revenue')
        .select('date, amount')
        .eq('user_id', user.id)
        .gte('date', format(monthStart, 'yyyy-MM-dd'))
        .lte('date', format(monthEnd, 'yyyy-MM-dd'));

      if (tipsData) {
        tipsData.forEach(tip => {
          if (!tipsMap[tip.date]) {
            tipsMap[tip.date] = 0;
          }
          tipsMap[tip.date] += tip.amount;
        });
      }
    }

    const pdf = new jsPDF();
    const monthName = format(date, 'MMMM yyyy', { locale: nl });

    // Set page margins and calculate content width
    const margin = 10;
    const pageWidth = pdf.internal.pageSize.width;
    const contentWidth = pageWidth - (2 * margin);

    pdf.setFont(FONT);
    pdf.setFontSize(STYLES.fontSize.title);
    pdf.setFont(FONT, 'bold');
    pdf.setTextColor(COLORS.text);
    pdf.text(`Maandrapport ${monthName}`, margin, 20);
    pdf.setFont(FONT, 'normal');

    // Add company info if available
    const { data: settings } = await supabase
      .from('settings')
      .select('company_name')
      .eq('user_id', user.id)
      .maybeSingle();

    if (settings?.company_name) {
      pdf.setFont(FONT);
      pdf.setFontSize(STYLES.fontSize.subtitle);
      pdf.setTextColor(COLORS.textSecondary);
      pdf.text(settings.company_name, margin, 26);
    }

    // Calculate totals including tips if enabled
    const totals = (data || []).reduce((acc, entry) => {
      if (selectedTypes.cash) acc.cash += entry.cash_revenue;
      if (selectedTypes.card) acc.card += entry.card_revenue;
      if (selectedTypes.invoice) acc.invoice += entry.invoice_revenue;
      const tipsForDate = selectedTypes.tips ? (tipsMap[entry.date] || 0) : 0;
      acc.tips += tipsForDate;
      acc.total = acc.cash + acc.card + acc.invoice + acc.tips;
      return acc;
    }, { cash: 0, card: 0, invoice: 0, tips: 0, total: 0 });

    // Draw summary cards
    const cardY = 28;
    const cardHeight = 16;
    const cardSpacing = 4;
    const cardWidth = (contentWidth - (3 * cardSpacing)) / 4;

    // Helper function to draw a card
    const drawCard = (x: number, title: string, value: string) => {
      // Card background
      pdf.setFillColor(COLORS.card);
      pdf.roundedRect(x, cardY, cardWidth, cardHeight, STYLES.cardRadius, STYLES.cardRadius, 'F');

      // Card content
      pdf.setFont(FONT);
      pdf.setFontSize(STYLES.fontSize.small);
      pdf.setFont(FONT, 'normal');
      pdf.setTextColor(COLORS.textSecondary);
      pdf.text(title, x + STYLES.cardPadding/2, cardY + 6);

      pdf.setFontSize(STYLES.fontSize.body);
      pdf.setFont(FONT, 'bold');
      pdf.setTextColor(COLORS.text);
      pdf.text(value, x + STYLES.cardPadding/2, cardY + 13);
      pdf.setFont(FONT, 'normal');
    };

    // Draw cards
    const startX = margin;
    let cardIndex = 0;
    drawCard(startX + (cardWidth + cardSpacing) * cardIndex++, 'Totaal', `€ ${totals.total.toFixed(2)}`);
    if (selectedTypes.cash) {
      drawCard(startX + (cardWidth + cardSpacing) * cardIndex++, 'Contant', `€ ${totals.cash.toFixed(2)}`);
    }
    if (selectedTypes.card) {
      drawCard(startX + (cardWidth + cardSpacing) * cardIndex++, 'Pin', `€ ${totals.card.toFixed(2)}`);
    }
    if (selectedTypes.invoice) {
      drawCard(startX + (cardWidth + cardSpacing) * cardIndex++, 'Rekening', `€ ${totals.invoice.toFixed(2)}`);
    }
    if (selectedTypes.tips && totals.tips > 0) {
      drawCard(startX + (cardWidth + cardSpacing) * cardIndex++, 'Fooi', `€ ${totals.tips.toFixed(2)}`);
    }

    // Table data
    const tableData = (data || []).map((entry: RevenueEntry) => {
      const tipsForDate = selectedTypes.tips ? (tipsMap[entry.date] || 0) : 0;
      const entryTotal = (
        (selectedTypes.cash ? entry.cash_revenue : 0) +
        (selectedTypes.card ? entry.card_revenue : 0) +
        (selectedTypes.invoice ? entry.invoice_revenue : 0) +
        tipsForDate
      );

      return [
        format(new Date(entry.date), 'dd/MM/yyyy'),
        ...(selectedTypes.cash ? [`€ ${entry.cash_revenue.toFixed(2)}`] : []),
        ...(selectedTypes.card ? [`€ ${entry.card_revenue.toFixed(2)}`] : []),
        ...(selectedTypes.invoice ? [`€ ${entry.invoice_revenue.toFixed(2)}`] : []),
        ...(selectedTypes.tips ? [`€ ${tipsForDate.toFixed(2)}`] : []),
        ...(Object.values(selectedTypes).filter(Boolean).length > 1 ? [`€ ${entryTotal.toFixed(2)}`] : []),
      ];
    });

    // Add totals row
    const totalsRow = [
      { content: 'Totaal', styles: { fontStyle: 'bold' } }
    ];
    if (selectedTypes.cash) {
      totalsRow.push({ content: `€ ${totals.cash.toFixed(2)}`, styles: { fontStyle: 'bold' } });
    }
    if (selectedTypes.card) {
      totalsRow.push({ content: `€ ${totals.card.toFixed(2)}`, styles: { fontStyle: 'bold' } });
    }
    if (selectedTypes.invoice) {
      totalsRow.push({ content: `€ ${totals.invoice.toFixed(2)}`, styles: { fontStyle: 'bold' } });
    }
    if (selectedTypes.tips) {
      totalsRow.push({ content: `€ ${totals.tips.toFixed(2)}`, styles: { fontStyle: 'bold' } });
    }
    if (Object.values(selectedTypes).filter(Boolean).length > 1) {
      totalsRow.push({ content: `€ ${totals.total.toFixed(2)}`, styles: { fontStyle: 'bold' } });
    }
    tableData.push(totalsRow);

    // Generate table
    const headers = ['Datum'];
    if (selectedTypes.cash) headers.push('Contant');
    if (selectedTypes.card) headers.push('Pin');
    if (selectedTypes.invoice) headers.push('Rekening');
    if (selectedTypes.tips) headers.push('Fooi');
    if (Object.values(selectedTypes).filter(Boolean).length > 1) {
      headers.push('Totaal');
    }

    // Calculate column widths based on number of columns
    const columnCount = headers.length;
    const columnWidth = contentWidth / columnCount;

    // Create column styles object
    const columnStyles = {};
    headers.forEach((_, index) => {
      columnStyles[index] = { 
        cellWidth: columnWidth, 
        halign: 'left' 
      };
    });

    autoTable(pdf, {
      head: [headers],
      body: tableData,
      startY: cardY + cardHeight + 8,
      theme: 'grid',
      margin: { left: margin, right: margin, top: margin, bottom: margin },
      styles: {
        lineColor: COLORS.border,
        font: FONT,
        fontSize: 9,
        textColor: COLORS.text,
        fillColor: COLORS.card,
        cellPadding: STYLES.tablePadding,
        lineWidth: 0.2,
        minCellHeight: 3,
        overflow: 'linebreak',
        halign: 'left'
      },
      headStyles: { 
        fillColor: COLORS.accent,
        textColor: COLORS.text,
        font: FONT,
        fontSize: 9,
        fontStyle: 'bold',
        cellPadding: STYLES.tablePadding,
        halign: 'left'
      },
      footStyles: { 
        fillColor: COLORS.card,
        textColor: COLORS.text,
        font: FONT,
        fontSize: 9,
        fontStyle: 'bold',
        cellPadding: STYLES.tablePadding,
        halign: 'left'
      },
      columnStyles,
      alternateRowStyles: {
        fillColor: '#FFFFFF',
      }
    });

    // Save PDF
    pdf.save(`maandrapport-${format(date, 'yyyy-MM')}.pdf`);
    setLoading(false);
  };

  const generateYearlyReport = async () => {
    setLoadingYearly(true);
    const year = parseInt(selectedYearlyYear);
    const yearStart = startOfYear(new Date(year, 0, 1));
    const yearEnd = endOfYear(new Date(year, 0, 1));

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoadingYearly(false); return; }

    const { data, error } = await supabase
      .from('revenue_entries')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', format(yearStart, 'yyyy-MM-dd'))
      .lte('date', format(yearEnd, 'yyyy-MM-dd'))
      .order('date', { ascending: true });

    if (error) { setLoadingYearly(false); return; }

    let tipsMap: Record<string, number> = {};
    if (selectedYearlyTypes.tips) {
      const { data: tipsData } = await supabase
        .from('hidden_revenue')
        .select('date, amount')
        .eq('user_id', user.id)
        .gte('date', format(yearStart, 'yyyy-MM-dd'))
        .lte('date', format(yearEnd, 'yyyy-MM-dd'));
      if (tipsData) {
        tipsData.forEach(tip => {
          tipsMap[tip.date] = (tipsMap[tip.date] || 0) + tip.amount;
        });
      }
    }

    const { data: settings } = await supabase
      .from('settings')
      .select('company_name')
      .eq('user_id', user.id)
      .maybeSingle();

    const pdf = new jsPDF();
    const margin = 10;
    const pageWidth = pdf.internal.pageSize.width;
    const contentWidth = pageWidth - 2 * margin;

    pdf.setFont(FONT, 'bold');
    pdf.setFontSize(STYLES.fontSize.title);
    pdf.setTextColor(COLORS.text);
    pdf.text(`Jaaroverzicht ${year}`, margin, 20);
    pdf.setFont(FONT, 'normal');

    if (settings?.company_name) {
      pdf.setFontSize(STYLES.fontSize.subtitle);
      pdf.setTextColor(COLORS.textSecondary);
      pdf.text(settings.company_name, margin, 26);
    }

    const months = eachMonthOfInterval({ start: yearStart, end: yearEnd });

    const monthlyData = months.map((monthDate) => {
      const monthStart = startOfMonth(monthDate);
      const monthEnd = endOfMonth(monthDate);
      const monthEntries = (data || []).filter(e => {
        const d = e.date;
        return d >= format(monthStart, 'yyyy-MM-dd') && d <= format(monthEnd, 'yyyy-MM-dd');
      });
      const cash = selectedYearlyTypes.cash ? monthEntries.reduce((s, e) => s + e.cash_revenue, 0) : 0;
      const card = selectedYearlyTypes.card ? monthEntries.reduce((s, e) => s + e.card_revenue, 0) : 0;
      const invoice = selectedYearlyTypes.invoice ? monthEntries.reduce((s, e) => s + e.invoice_revenue, 0) : 0;
      const tips = selectedYearlyTypes.tips ? monthEntries.reduce((s, e) => s + (tipsMap[e.date] || 0), 0) : 0;
      const total = cash + card + invoice + tips;
      return { monthDate, cash, card, invoice, tips, total };
    });

    const yearTotals = monthlyData.reduce(
      (acc, m) => ({ cash: acc.cash + m.cash, card: acc.card + m.card, invoice: acc.invoice + m.invoice, tips: acc.tips + m.tips, total: acc.total + m.total }),
      { cash: 0, card: 0, invoice: 0, tips: 0, total: 0 }
    );

    const cardY = 28;
    const cardHeight = 16;
    const cardSpacing = 4;
    const cardWidth = (contentWidth - 3 * cardSpacing) / 4;

    const drawCard = (x: number, title: string, value: string) => {
      pdf.setFillColor(COLORS.card);
      pdf.roundedRect(x, cardY, cardWidth, cardHeight, STYLES.cardRadius, STYLES.cardRadius, 'F');
      pdf.setFontSize(STYLES.fontSize.small);
      pdf.setFont(FONT, 'normal');
      pdf.setTextColor(COLORS.textSecondary);
      pdf.text(title, x + STYLES.cardPadding / 2, cardY + 6);
      pdf.setFontSize(STYLES.fontSize.body);
      pdf.setFont(FONT, 'bold');
      pdf.setTextColor(COLORS.text);
      pdf.text(value, x + STYLES.cardPadding / 2, cardY + 13);
      pdf.setFont(FONT, 'normal');
    };

    let cardIndex = 0;
    drawCard(margin + (cardWidth + cardSpacing) * cardIndex++, 'Jaartotaal', `€ ${yearTotals.total.toFixed(2)}`);
    if (selectedYearlyTypes.cash) drawCard(margin + (cardWidth + cardSpacing) * cardIndex++, 'Contant', `€ ${yearTotals.cash.toFixed(2)}`);
    if (selectedYearlyTypes.card) drawCard(margin + (cardWidth + cardSpacing) * cardIndex++, 'Pin', `€ ${yearTotals.card.toFixed(2)}`);
    if (selectedYearlyTypes.invoice) drawCard(margin + (cardWidth + cardSpacing) * cardIndex++, 'Rekening', `€ ${yearTotals.invoice.toFixed(2)}`);

    const headers = ['Maand'];
    if (selectedYearlyTypes.cash) headers.push('Contant');
    if (selectedYearlyTypes.card) headers.push('Pin');
    if (selectedYearlyTypes.invoice) headers.push('Rekening');
    if (selectedYearlyTypes.tips) headers.push('Fooi');
    headers.push('Totaal');

    const tableData = monthlyData.map(m => [
      format(m.monthDate, 'MMMM', { locale: nl }),
      ...(selectedYearlyTypes.cash ? [`€ ${m.cash.toFixed(2)}`] : []),
      ...(selectedYearlyTypes.card ? [`€ ${m.card.toFixed(2)}`] : []),
      ...(selectedYearlyTypes.invoice ? [`€ ${m.invoice.toFixed(2)}`] : []),
      ...(selectedYearlyTypes.tips ? [`€ ${m.tips.toFixed(2)}`] : []),
      `€ ${m.total.toFixed(2)}`,
    ]);

    const totalsRow: any[] = [
      { content: 'Totaal', styles: { fontStyle: 'bold' } },
      ...(selectedYearlyTypes.cash ? [{ content: `€ ${yearTotals.cash.toFixed(2)}`, styles: { fontStyle: 'bold' } }] : []),
      ...(selectedYearlyTypes.card ? [{ content: `€ ${yearTotals.card.toFixed(2)}`, styles: { fontStyle: 'bold' } }] : []),
      ...(selectedYearlyTypes.invoice ? [{ content: `€ ${yearTotals.invoice.toFixed(2)}`, styles: { fontStyle: 'bold' } }] : []),
      ...(selectedYearlyTypes.tips ? [{ content: `€ ${yearTotals.tips.toFixed(2)}`, styles: { fontStyle: 'bold' } }] : []),
      { content: `€ ${yearTotals.total.toFixed(2)}`, styles: { fontStyle: 'bold' } },
    ];
    tableData.push(totalsRow);

    const columnWidth = contentWidth / headers.length;
    const columnStyles: any = {};
    headers.forEach((_, i) => { columnStyles[i] = { cellWidth: columnWidth, halign: 'left' }; });

    autoTable(pdf, {
      head: [headers],
      body: tableData,
      startY: cardY + cardHeight + 8,
      theme: 'grid',
      margin: { left: margin, right: margin, top: margin, bottom: margin },
      styles: { lineColor: COLORS.border, font: FONT, fontSize: 9, textColor: COLORS.text, fillColor: COLORS.card, cellPadding: STYLES.tablePadding, lineWidth: 0.2, minCellHeight: 3, overflow: 'linebreak', halign: 'left' },
      headStyles: { fillColor: COLORS.accent, textColor: COLORS.text, font: FONT, fontSize: 9, fontStyle: 'bold', cellPadding: STYLES.tablePadding, halign: 'left' },
      columnStyles,
      alternateRowStyles: { fillColor: '#FFFFFF' },
    });

    pdf.save(`jaaroverzicht-${year}.pdf`);
    setLoadingYearly(false);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Omzetrapport</h1>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Year and Month Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Jaar
              </label>
              <select
                id="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-3 pl-4 pr-10 text-base text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-sm"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="month" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Maand
              </label>
              <select
                id="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-3 pl-4 pr-10 text-base text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-sm"
              >
                {getAvailableMonths().map((month) => (
                  <option key={month} value={month.toString().padStart(2, '0')}>
                    {format(new Date(parseInt(selectedYear), month - 1, 1), 'MMMM', { locale: nl })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Revenue Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Omzettypen
            </label>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setSelectedTypes(prev => ({ ...prev, cash: !prev.cash }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  selectedTypes.cash
                    ? 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                Contant
              </button>
              <button
                type="button"
                onClick={() => setSelectedTypes(prev => ({ ...prev, card: !prev.card }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  selectedTypes.card
                    ? 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                Pin
              </button>
              <button
                type="button"
                onClick={() => setSelectedTypes(prev => ({ ...prev, invoice: !prev.invoice }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  selectedTypes.invoice
                    ? 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                Rekening
              </button>
              <button
                type="button"
                onClick={() => setSelectedTypes(prev => ({ ...prev, tips: !prev.tips }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  selectedTypes.tips
                    ? 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                Fooi
              </button>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateMonthlyReport}
            disabled={loading || (!selectedTypes.cash && !selectedTypes.card && !selectedTypes.invoice && !selectedTypes.tips)}
            className="w-full flex items-center justify-center px-6 py-3 rounded-lg text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 dark:focus:ring-offset-gray-800 transition-colors duration-200"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Download className="h-5 w-5 mr-2" />
                Genereer Maandrapport
              </>
            )}
          </button>
        </div>
      </div>

      {/* Yearly Report */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Jaaroverzicht
        </h2>
        <div className="space-y-6">
          <div>
            <label htmlFor="yearly-year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Jaar
            </label>
            <select
              id="yearly-year"
              value={selectedYearlyYear}
              onChange={(e) => setSelectedYearlyYear(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-3 pl-4 pr-10 text-base text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-sm"
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Omzettypen
            </label>
            <div className="flex flex-wrap gap-3">
              {(['cash', 'card', 'invoice', 'tips'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setSelectedYearlyTypes(prev => ({ ...prev, [type]: !prev[type] }))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    selectedYearlyTypes[type]
                      ? 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {type === 'cash' ? 'Contant' : type === 'card' ? 'Pin' : type === 'invoice' ? 'Rekening' : 'Fooi'}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={generateYearlyReport}
            disabled={loadingYearly || (!selectedYearlyTypes.cash && !selectedYearlyTypes.card && !selectedYearlyTypes.invoice && !selectedYearlyTypes.tips)}
            className="w-full flex items-center justify-center px-6 py-3 rounded-lg text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 dark:focus:ring-offset-gray-800 transition-colors duration-200"
          >
            {loadingYearly ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Download className="h-5 w-5 mr-2" />
                Genereer Jaaroverzicht
              </>
            )}
          </button>
        </div>
      </div>
      <ComparisonView />
    </div>
  );
}